import type { Map as MlMap, GeoJSONSource, LngLatBoundsLike } from "maplibre-gl";
import { frameAt, trackSpan, ms, thaiWhen, stormTitle, windText, fromDirection, type PastStorm } from "./past-storm-core";
import { ambientEnabled, setAmbientEnabled } from "./ambient-flow";
import { ensureStormIcon } from "./storm-icon";

/**
 * v0.9.6 C3–C4 — "ดูพายุในอดีต": ☰ → list → pick → ▶ plays the REAL track (IBTrACS) on the
 * event's own calendar. While it plays, every live layer that could be mistaken for it is hidden
 * (rain forecast fill, dam ripples / water dots, live storm, day bars, dams / staffs / gauges /
 * satellite flood, the result route) and restored exactly on close. A permanent banner names the
 * storm "(ข้อมูลย้อนหลัง)" with its source. Writes NOTHING to the summary / result (C5) — this
 * module does not import them.
 */
interface Index {
  source: string; citation: string; citationUrl: string;
  storms: { sid: string; nameEn: string; nameTh: string | null; yearBE: number; file: string }[];
  andamanNote?: { text: string };
}

const SEC_PER_REAL_SEC = 3 * 3600;   // 1 s on screen = 3 h of the event (×speed)
const REDUCED_STEP_MS = 1400;        // reduced motion: one still frame per day
const DAY = 86400000;
// live layers that could mix with a past event (by id prefix — GISTDA ids are per window) — hidden
// while playing, restored after. Rivers, river names and the basemap stay.
const LIVE_PREFIXES = ["prov-rain", "dams-", "tw-", "bkk-", "gistda", "radius-", "route-", "user-pin-block", "dam-flow", "dr-", "storm-", "amb-", "fa-"];
const SRC_LINE = "past-track", SRC_PTS = "past-pts", SRC_CUR = "past-cur";

let index: Index | null = null;
let active: { map: MlMap; storm: PastStorm; saved: Map<string, string>; ambient: boolean } | null = null;

export function pastModeActive(): boolean { return active != null; }

async function loadIndex(): Promise<Index | null> {
  if (index) return index;
  try { const r = await fetch("/data/storms-past/index.json"); if (r.ok) index = (await r.json()) as Index; } catch { /* none */ }
  return index;
}

/** ☰ → "ดูพายุในอดีต": the list sheet. */
export async function openPastStormMenu(map: MlMap): Promise<void> {
  document.querySelector(".past-menu")?.remove();
  const idx = await loadIndex();
  const box = document.createElement("div");
  box.className = "past-menu"; box.setAttribute("role", "dialog"); box.setAttribute("aria-label", "ดูพายุในอดีต");
  if (!idx || !idx.storms.length) {
    box.innerHTML = `<div class="pm-head"><b>🌀 ดูพายุในอดีต</b><button class="pm-close" aria-label="ปิด">✕</button></div><div class="muted">ยังไม่มีข้อมูลพายุในอดีต</div>`;
  } else {
    box.innerHTML = `<div class="pm-head"><b>🌀 ดูพายุในอดีต</b><button class="pm-close" aria-label="ปิด">✕</button></div>
      <div class="muted small">เส้นทางจริงของพายุที่เคลื่อนเข้าไทย (ข้อมูลย้อนหลัง)</div>
      <div class="pm-list">${idx.storms.map((s) => `<button class="pm-item" data-sid="${s.sid}">${s.nameTh ? `${esc(s.nameTh)} (${esc(s.nameEn.replace(/:/g, " / "))})` : esc(s.nameEn.replace(/:/g, " / "))} <span class="muted">ปี ${s.yearBE}</span></button>`).join("")}</div>
      ${idx.andamanNote ? `<div class="muted small pm-note">${esc(idx.andamanNote.text)}</div>` : ""}
      <div class="muted small pm-src">ที่มา: <a href="${idx.citationUrl}" target="_blank" rel="noopener">${esc(idx.citation)}</a></div>`;
  }
  box.querySelector(".pm-close")?.addEventListener("click", () => box.remove());
  box.querySelectorAll<HTMLElement>(".pm-item").forEach((b) => b.addEventListener("click", () => {
    const s = idx!.storms.find((x) => x.sid === b.dataset.sid); if (!s) return;
    box.remove(); void startPastStorm(map, s.file);
  }));
  document.body.append(box);
}

export async function startPastStorm(map: MlMap, file: string): Promise<void> {
  let storm: PastStorm;
  try { const r = await fetch(`/data/storms-past/${file}`); if (!r.ok) return; storm = (await r.json()) as PastStorm; } catch { return; }
  if (active) stopPastStorm();
  const saved = new Map<string, string>();
  for (const l of map.getStyle().layers ?? []) {
    if (!LIVE_PREFIXES.some((p) => l.id.startsWith(p))) continue;
    saved.set(l.id, String(map.getLayoutProperty(l.id, "visibility") ?? "visible"));
    map.setLayoutProperty(l.id, "visibility", "none");
  }
  const amb = ambientEnabled();
  if (amb) setAmbientEnabled(false);
  document.body.classList.add("past-mode");
  active = { map, storm, saved, ambient: amb };
  await ensureStormIcon(map);
  drawTrack(map, storm);
  const [t0, t1] = trackSpan(storm);
  runPlayer(storm, t0, t1);
  // frame the track over Indochina / the near seas (+1°) above the player bar — measured, so the
  // whole approach and the part over Thailand stay in the free map area on every screen
  const near = storm.points.filter((p) => p.lon > 97 && p.lon < 112 && p.lat > 4 && p.lat < 23);
  const use = near.length >= 2 ? near : storm.points;
  const lons = use.map((p) => p.lon), lats = use.map((p) => p.lat);
  const barH = bar?.getBoundingClientRect().height ?? 200;
  map.fitBounds([[Math.min(...lons) - 1, Math.min(...lats) - 1], [Math.max(...lons) + 1, Math.max(...lats) + 1]] as LngLatBoundsLike,
    { padding: { top: 80, bottom: Math.round(barH) + 24, left: 16, right: window.innerWidth >= 820 ? 420 : 16 }, maxZoom: 7, duration: 0 });
}

export function stopPastStorm(): void {
  if (!active) return;
  const { map, saved, ambient } = active;
  cancelAnimationFrame(raf); raf = 0;
  for (const id of [SRC_CUR, SRC_PTS, SRC_LINE]) {
    for (const l of [`${id}-l`, `${id}-c`, `${id}-s`, `${id}-t`]) if (map.getLayer(l)) map.removeLayer(l);
    if (map.getSource(id)) map.removeSource(id);
  }
  for (const [id, v] of saved) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v as "visible" | "none");
  if (ambient) setAmbientEnabled(true);
  document.body.classList.remove("past-mode");
  bar?.remove(); bar = null;
  active = null;
}

function drawTrack(map: MlMap, s: PastStorm): void {
  const line: GeoJSON.Feature = { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: s.points.map((p) => [p.lon, p.lat]) } };
  map.addSource(SRC_LINE, { type: "geojson", data: { type: "FeatureCollection", features: [line] } });
  map.addLayer({ id: `${SRC_LINE}-l`, type: "line", source: SRC_LINE, paint: { "line-color": "#AFA9EC", "line-width": 2.6, "line-opacity": 0.9 }, layout: { "line-cap": "round", "line-join": "round" } });
  // source points (interpolated IBTrACS points drawn smaller); a label once per Thai day at 07:00 ICT (00Z)
  const feats: GeoJSON.Feature[] = s.points.map((p) => {
    const t = ms(p.t), d = new Date(t + 7 * 3600 * 1000);
    return { type: "Feature", properties: { th: p.inThailand, ip: p.interpolated, label: !p.interpolated && d.getUTCHours() === 7 ? thaiWhen(t, false).replace(/ (เช้า|บ่าย|ค่ำ|กลางคืน)$/, "") : "" },
      geometry: { type: "Point", coordinates: [p.lon, p.lat] } };
  });
  map.addSource(SRC_PTS, { type: "geojson", data: { type: "FeatureCollection", features: feats } });
  map.addLayer({ id: `${SRC_PTS}-c`, type: "circle", source: SRC_PTS, paint: {
    "circle-radius": ["case", ["get", "ip"], 2, ["get", "th"], 4.2, 3.2], "circle-color": ["case", ["get", "th"], "#ff8fb3", "#AFA9EC"],
    "circle-stroke-color": "#05080f", "circle-stroke-width": 1 } });
  map.addLayer({ id: `${SRC_PTS}-t`, type: "symbol", source: SRC_PTS, filter: ["!=", ["get", "label"], ""], layout: {
    "text-field": ["get", "label"], "text-font": ["Noto Sans Regular"], "text-size": 10, "text-offset": [0, -0.9], "text-anchor": "bottom", "text-optional": true },
    paint: { "text-color": "#d9d5f5", "text-halo-color": "#05080f", "text-halo-width": 1.4 } });
  map.addSource(SRC_CUR, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addLayer({ id: `${SRC_CUR}-s`, type: "symbol", source: SRC_CUR, layout: {
    "icon-image": "storm-icon", "icon-size": 0.62, "icon-rotate": ["get", "rot"], "icon-rotation-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true },
    paint: { "icon-opacity": ["get", "op"] } });
}

/* ── the player (calendar time of the event) ─────────────────────────────── */
let bar: HTMLElement | null = null;
let raf = 0;

function runPlayer(s: PastStorm, t0: number, t1: number): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let t = t0, playing = true, speed = 1, last = 0, spin = 0, stepAt = 0;
  const entry = ms(s.entryUtc);
  bar?.remove();
  bar = document.createElement("div");
  bar.className = "fa-bar past-bar";
  bar.innerHTML = `
    <div class="past-top"><div class="past-banner">🌀 พายุ${esc(stormTitle(s))} <b>(ข้อมูลย้อนหลัง)</b></div>
      <button class="fa-btn past-close" data-a="close" aria-label="ปิดโหมดพายุในอดีต">✕</button></div>
    <div class="fa-row">
      <button class="fa-btn" data-a="play" aria-label="เล่น/หยุด">⏸</button>
      <input class="fa-range" type="range" min="0" max="1000" value="0" aria-label="ไทม์ไลน์" />
      <span class="fa-speeds"><button class="fa-sp on" data-sp="1">1×</button><button class="fa-sp" data-sp="2">2×</button><button class="fa-sp" data-sp="4">4×</button></span>
    </div>
    <div class="past-when"></div>
    <div class="past-info"></div>
    <div class="past-src">ที่มา: <a href="${s.citationUrl}" target="_blank" rel="noopener">${esc(s.citation)}</a> · <a href="${s.dataUrl}" target="_blank" rel="noopener">ข้อมูลเส้นทาง</a>${s.nameThSource ? ` · ชื่อไทย: <a href="${s.nameThSource.url}" target="_blank" rel="noopener">กรมอุตุนิยมวิทยา</a>` : ""}</div>`;
  const range = bar.querySelector<HTMLInputElement>(".fa-range")!, playBtn = bar.querySelector<HTMLElement>('[data-a="play"]')!;
  playBtn.addEventListener("click", () => { if (t >= t1) t = t0; playing = !playing; });
  bar.querySelector('[data-a="close"]')!.addEventListener("click", () => stopPastStorm());
  range.addEventListener("input", () => { t = t0 + (Number(range.value) / 1000) * (t1 - t0); playing = false; });
  bar.querySelectorAll<HTMLElement>(".fa-sp").forEach((el) => el.addEventListener("click", () => {
    speed = Number(el.dataset.sp); bar!.querySelectorAll(".fa-sp").forEach((x) => x.classList.toggle("on", x === el));
  }));
  document.body.append(bar);

  const frame = (ts: number): void => {
    if (!active) return;
    const dt = last ? ts - last : 0; last = ts;
    if (playing) {
      if (reduced) { if (ts - stepAt >= REDUCED_STEP_MS) { stepAt = ts; t = Math.min(t1, t + DAY); } }
      else t = Math.min(t1, t + (dt / 1000) * SEC_PER_REAL_SEC * 1000 * speed);
      if (t >= t1) playing = false;
    }
    if (!reduced) spin = (spin - dt * 0.12) % 360; // counter-clockwise (northern hemisphere)
    const f = frameAt(s, t);
    (active.map.getSource(SRC_CUR) as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: [
      { type: "Feature", properties: { rot: spin, op: f.ended ? 0 : 1 }, geometry: { type: "Point", coordinates: [f.lon, f.lat] } }] });
    range.value = String(Math.round(((t - t0) / Math.max(1, t1 - t0)) * 1000));
    bar!.querySelector(".past-when")!.textContent = thaiWhen(t);
    playBtn.textContent = playing ? "⏸" : "▶";
    const where = f.inThailand ? "อยู่บนแผ่นดินไทย" : t < entry ? `จะเข้าไทยจากทาง${fromDirection(s.entryBearingDeg)}` : "พ้นประเทศไทยแล้ว";
    bar!.querySelector(".past-info")!.textContent = `${windText(f.wind)} · ${where}${t >= t1 ? " · สิ้นสุดข้อมูลเส้นทาง" : ""}`;
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
}

const esc = (x: string): string => x.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));
